import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import TradeListService from '../../../services/TradeListService';
import FilterService from '../../../services/FilterService';
import SettingsService from '../../../services/SettingsService';
import MixpanelService from '../../../services/MixpanelService';
import MixpanelConfig from '../../../_shared/constants/mixpanelConfig';
import ConfirmModal from '../../../_shared/components/ConfirmModal';
import {
  FILTER_SETTINGS,
  FILTER_NOTIFICATIONS
} from '../../../_shared/constants/userSettings';
import Toggle from '../../../_shared/components/Toggle';
import Alert from '../../../_shared/components/Alert';
import { FilterNames, TradeStateName } from '../../../_shared/types/trade';
import { ListVariantSource } from '../../../_shared/types/auction';

import {
  StyledNotifications,
  StyledNotificationsToggle,
  StyledFilterNameLabel,
  StyledFilterNameInput,
  StyledError,
  StyledApplyMessage
} from './Styles';
import TRToast from '../../../_shared/helpers/TRToast';

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  updateFilterList: PropTypes.func.isRequired,
  activeFilter: PropTypes.objectOf(PropTypes.any).isRequired,
  filterList: PropTypes.arrayOf(PropTypes.object).isRequired,
  tradeState: PropTypes.number.isRequired
};

const ConfirmModalContainer = ({
  isOpen,
  setIsOpen,
  updateFilterList,
  activeFilter,
  filterList,
  tradeState,
  enableNotification,
  setEnableNotification,
  settings,
  getSettings
}) => {
  const mixpanel = MixpanelService.get();
  const settingsService = SettingsService.get();
  const [isAllFilter, setIsAllFilter] = useState(
    activeFilter?.id === FilterNames.all.id
  );

  useEffect(() => {
    const isAllFilterSelected = activeFilter?.id === FilterNames.all.id;
    if (isAllFilterSelected) {
      setEnableNotification(true);
    }
    if (isAllFilterSelected !== isAllFilter)
      setIsAllFilter(isAllFilterSelected);
  }, [activeFilter?.id, isAllFilter, setEnableNotification]);

  const [previewFilterCriteria, listVariant] = useSelector(
    state => [
      state?.filter?.previewCriteria?.criteria,
      state?.ui?.layout?.listVariant
    ],
    shallowEqual
  );

  const intl = useIntl();
  const [filterTitle, setFilterTitle] = useState('');
  const [modalLogic, setModalLogic] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleToggle = () => {
    setEnableNotification(notification => !notification);
    mixpanel.track(MixpanelConfig.names.filterNotification, {
      toggle_on: !enableNotification,
      trade_state: TradeStateName[tradeState],
      list_type: ListVariantSource[listVariant]
    });
  };

  const [canContinue, setCanContinue] = useState(false);

  const handleChange = e => {
    setFilterTitle(e.target.value);
  };

  useEffect(() => {
    setCanContinue(!!filterTitle?.length);
  }, [filterTitle?.length]);

  const [error, setError] = useState('');

  // Resets the modal state back to initial
  const resetModalLogic = useCallback(
    notificationState => {
      setModalLogic([]);
      setEnableNotification(
        typeof notificationState === 'boolean' ? notificationState : true
      );
      setError('');
      setCanContinue(false);
    },
    [setEnableNotification]
  );

  const isFormValid = useCallback(
    (title, _setError) => {
      if (title?.length <= 0) {
        _setError(intl.formatMessage({ id: 'list.filters.save.noFilterName' }));
        return false;
      }
      if (
        filterList &&
        FilterService.isDuplicateFilterName(filterList, title)
      ) {
        _setError(
          intl.formatMessage(
            {
              id: 'list.filters.save.duplicateFilter'
            },
            {
              name: title
            }
          )
        );
        return false;
      }

      return true;
    },
    [filterList, intl]
  );

  // Create the saved filter
  const createFilter = useCallback(
    async options => {
      setIsLoading(true);
      const {
        criteria,
        onCreateFilter,
        isCreate,
        id,
        isCustomNotificationDisabled
      } = options;
      const tradeListService = TradeListService.get();
      let response;

      // If creating a new filter
      if (isCreate) {
        response = await tradeListService.createList({
          criteria,
          title: filterTitle,
          enableNotification: isCustomNotificationDisabled
            ? false
            : enableNotification
        });
      } else {
        response = await tradeListService.updateList({
          id,
          criteria,
          enableNotification
        });
      }

      if (response?.ok) {
        if (enableNotification && !isCustomNotificationDisabled) {
          TRToast(
            <Alert
              variant="success"
              messages={[
                <FormattedMessage id="list.filters.notification.success" />
              ]}
            />
          );
        } else {
          TRToast(
            <Alert
              variant="success"
              messages={[<FormattedMessage id="list.filters.save.success" />]}
            />
          );
        }

        // add newly created filter to the saved filter list
        onCreateFilter({
          id: response?.body?.id,
          title: filterTitle,
          enableNotification
        });
      } else {
        // eslint-disable-next-line
        if (enableNotification && !isCustomNotificationDisabled) {
          TRToast(
            <Alert
              variant="error"
              messages={[
                <FormattedMessage id="list.filters.notification.error" />
              ]}
            />
          );
        } else {
          TRToast(
            <Alert
              variant="error"
              messages={[<FormattedMessage id="list.filters.save.error" />]}
            />
          );
        }
      }

      setIsLoading(false);
      setIsOpen(false);
      resetModalLogic(isCustomNotificationDisabled);
    },
    [enableNotification, filterTitle, resetModalLogic, setIsOpen]
  );

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    resetModalLogic();
  }, [setIsOpen, resetModalLogic]);

  // Handler for if user has custom settings and they clicked Yes
  const onCustomConfirm = useCallback(async () => {
    const settingsToAdd = {
      email: {
        buyer: [FILTER_NOTIFICATIONS.upcoming, FILTER_NOTIFICATIONS.active]
      },
      push: {
        buyer: [FILTER_NOTIFICATIONS.upcoming, FILTER_NOTIFICATIONS.active]
      },
      sms: {
        buyer: [FILTER_NOTIFICATIONS.upcoming, FILTER_NOTIFICATIONS.active]
      }
    };
    const updateSettings = await settingsService.updateNotificationSettings(
      settingsToAdd,
      FILTER_SETTINGS.custom
    );

    if (updateSettings?.ok) {
      getSettings();
      TRToast(
        <Alert
          variant="success"
          messages={[
            <FormattedMessage id="list.filters.updateNotification.success" />
          ]}
        />
      );
    } else {
      TRToast(
        <Alert
          variant="error"
          messages={[
            <FormattedMessage id="list.filters.updateNotification.error" />
          ]}
        />
      );
    }

    createFilter({
      criteria: previewFilterCriteria,
      title: filterTitle,
      isCreate: isAllFilter,
      onCreateFilter: updateFilterList,
      enableNotification
    });
  }, [
    settingsService,
    createFilter,
    previewFilterCriteria,
    filterTitle,
    isAllFilter,
    updateFilterList,
    enableNotification,
    getSettings
  ]);

  // Handler for if user has custom settings and they clicked No thanks
  const onCustomCancel = useCallback(() => {
    createFilter({
      criteria: previewFilterCriteria,
      title: filterTitle,
      isCreate: isAllFilter,
      onCreateFilter: updateFilterList,
      isCustomNotificationDisabled: true
    });
  }, [
    createFilter,
    filterTitle,
    isAllFilter,
    previewFilterCriteria,
    updateFilterList
  ]);

  // Handler for if user has no notifications (off)
  const onNoNotificationConfirm = useCallback(() => {
    createFilter({
      criteria: previewFilterCriteria,
      title: filterTitle,
      isCustomNotificationDisabled: true,
      onCreateFilter: updateFilterList,
      isCreate: isAllFilter
    });
  }, [
    createFilter,
    filterTitle,
    isAllFilter,
    previewFilterCriteria,
    updateFilterList
  ]);

  // Handler for first confirmation modal's confirm button
  const handleConfirm = useCallback(() => {
    // Validate if filter name field is filled
    const formValid = isFormValid(filterTitle, setError);

    if (formValid) {
      setError('');

      if (modalLogic?.length === 1) {
        // Otherwise, just create the new filter
        createFilter({
          criteria: previewFilterCriteria,
          title: filterTitle,
          enableNotification,
          onCreateFilter: updateFilterList,
          isCreate: isAllFilter,
          id: activeFilter.id
        });
      }
    }
  }, [
    activeFilter.id,
    createFilter,
    enableNotification,
    filterTitle,
    isAllFilter,
    isFormValid,
    modalLogic?.length,
    previewFilterCriteria,
    updateFilterList
  ]);

  // Find out if user turned on custom and active/upcoming filter notifications
  useEffect(() => {
    if (settings?.userNotificationSettings && modalLogic?.length >= 1) {
      const customNoNotifications = () => {
        const notificationMethods =
          settings?.userNotificationSettings?.notificationMethods;

        return Object.keys(notificationMethods)?.some(method => {
          if (
            notificationMethods[method].buyer.includes(
              FILTER_NOTIFICATIONS.upcoming
            ) ||
            notificationMethods[method].buyer.includes(
              FILTER_NOTIFICATIONS.active
            )
          ) {
            return true;
          }
          return false;
        });
      };
      let newSecond;

      // Append next confirmation modal based on user's notification settings
      if (
        settings?.userNotificationSettings?.presetId ===
          FILTER_SETTINGS.custom &&
        !customNoNotifications() &&
        enableNotification
      ) {
        // If user has notifications on 'Custom' and does not have active/upcoming filter notifications on
        newSecond = {
          title: <FormattedMessage id="list.filters.save.custom.title" />,
          msg: <FormattedMessage id="list.filters.save.custom.msg" />,
          confirmLabel: <FormattedMessage id="modal.yes" />,
          onConfirm: () => onCustomConfirm,
          cancelLabel: <FormattedMessage id="modal.noThanks" />,
          onCancel: () => onCustomCancel
        };
      } else if (
        // Otherwise if user has notifications completely off
        settings?.userNotificationSettings?.presetId === FILTER_SETTINGS.off &&
        enableNotification
      ) {
        newSecond = {
          title: (
            <FormattedMessage id="list.filters.save.notificationsOff.title" />
          ),
          msg: <FormattedMessage id="list.filters.save.notificationsOff.msg" />,
          confirmLabel: <FormattedMessage id="modal.ok" />,
          onConfirm: () => onNoNotificationConfirm
        };
      }

      if (newSecond) setModalLogic(content => [content[0], newSecond]);
      else setModalLogic(content => [content[0]]);
    }
  }, [
    enableNotification,
    modalLogic?.length,
    onCustomCancel,
    onCustomConfirm,
    onNoNotificationConfirm,
    settings?.userNotificationSettings
  ]);

  const handleConfirmApply = useCallback(() => {
    createFilter({
      criteria: previewFilterCriteria,
      onCreateFilter: updateFilterList,
      isCreate: isAllFilter,
      id: activeFilter.id
    });
  }, [
    createFilter,
    activeFilter,
    isAllFilter,
    previewFilterCriteria,
    updateFilterList
  ]);

  const msgCallback = useCallback(
    (isAll, name) => (
      <>
        {isAll && (
          <>
            <FormattedMessage id="list.filters.save.confirmation.msg" />
            <StyledNotifications>
              <StyledFilterNameLabel>
                <FormattedMessage id="list.filters.save.confirmation.newName" />
              </StyledFilterNameLabel>
              {error && <StyledError hasError={error}>{error}</StyledError>}
              <StyledFilterNameInput
                data-testid="filter-name-input-text"
                hasError={error}
                onChange={handleChange}
                placeholder={intl.formatMessage({
                  id: 'list.filters.save.confirmation.placeholder'
                })}
                maxLength="25"
                autoFocus
              />
            </StyledNotifications>
            <StyledNotificationsToggle>
              <Toggle onClick={handleToggle} isChecked={enableNotification}>
                <FormattedMessage id="list.filters.notification.label" />
              </Toggle>
            </StyledNotificationsToggle>
          </>
        )}

        {!isAll && (
          <>
            <StyledApplyMessage>
              <FormattedMessage
                id="list.filters.apply.body.message"
                values={{ name }}
              />
            </StyledApplyMessage>
          </>
        )}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enableNotification, error, intl]
  );

  // Initializes and triggers when the input validation changes
  useEffect(() => {
    const newInitial = {
      title: isAllFilter ? (
        <FormattedMessage id="list.filters.save.confirmation.title" />
      ) : (
        <FormattedMessage id="list.filters.apply.confirmation.title" />
      ),
      msg: msgCallback(isAllFilter, activeFilter?.title),
      confirmLabel: isAllFilter ? (
        <FormattedMessage id="list.filters.save" />
      ) : (
        <FormattedMessage id="list.filters.overwrite" />
      ),
      cancelLabel: <FormattedMessage id="modal.cancel" />,
      onConfirm: isAllFilter ? () => handleConfirm : () => handleConfirmApply,
      onCancel: () => handleCancel
    };

    if (!isAllFilter) {
      setTimeout(() => {
        setModalLogic(modals => [newInitial, ...modals.slice(1)]);
      }, 250);
    } else {
      setModalLogic(modals => [newInitial, ...modals.slice(1)]);
    }
  }, [
    activeFilter?.title,
    handleCancel,
    handleConfirm,
    handleConfirmApply,
    isAllFilter,
    msgCallback
  ]);

  return (
    <ConfirmModal
      isOpen={isOpen}
      modalLogic={modalLogic}
      isLoading={isLoading}
      loadingMsg={<FormattedMessage id="modal.confirmation.loading" />}
      canContinue={canContinue && !error?.length}
    />
  );
};

ConfirmModalContainer.propTypes = propTypes;

export default ConfirmModalContainer;
